"use client";

import { type CSSProperties, useEffect, useMemo, useRef } from "react";

type Props = {
	html: string;
	className?: string;
	style?: CSSProperties;
};

const clipboardIcon = `<svg viewBox="0 0 24 24" aria-hidden="true"><rect width="14" height="16" x="5" y="5" rx="2" ry="2"></rect><path d="M9 5V3h6v2"></path></svg>`;

const checkIcon = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6"></path></svg>`;

async function copyText(text: string) {
	if (navigator.clipboard?.writeText) {
		await navigator.clipboard.writeText(text);
		return;
	}

	const textarea = document.createElement("textarea");
	textarea.value = text;
	textarea.style.position = "fixed";
	textarea.style.opacity = "0";
	document.body.appendChild(textarea);
	textarea.select();
	document.execCommand("copy");
	textarea.remove();
}

export default function CopyableCodeContent({ html, className, style }: Props) {
	const contentRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const root = contentRef.current;
		if (!root) return;

		const resetTimers = new Set<ReturnType<typeof setTimeout>>();

		// Wrap each <pre> in a relative container and move copy button outside <pre>
		root.querySelectorAll<HTMLPreElement>("pre").forEach((pre) => {
			if (pre.parentElement?.classList.contains("code-block-wrapper")) return;

			const wrapper = document.createElement("div");
			wrapper.className = "code-block-wrapper";
			pre.parentNode?.insertBefore(wrapper, pre);
			wrapper.appendChild(pre);

			const button = document.createElement("button");
			button.type = "button";
			button.className = "code-copy-button";
			button.setAttribute("aria-label", "Salin kode");
			button.title = "Salin kode";
			button.innerHTML = clipboardIcon;
			wrapper.appendChild(button);
		});

		const handleClick = async (event: MouseEvent) => {
			const target = event.target as Element;
			const button = target.closest<HTMLButtonElement>(".code-copy-button");
			if (!button || !root.contains(button)) return;

			const wrapper = button.closest(".code-block-wrapper");
			const code = wrapper?.querySelector("pre code");
			if (!code) return;

			try {
				await copyText(code.textContent ?? "");
				button.classList.add("is-copied");
				button.setAttribute("aria-label", "Kode tersalin");
				button.title = "Tersalin";
				button.innerHTML = checkIcon;

				const timer = setTimeout(() => {
					button.classList.remove("is-copied");
					button.setAttribute("aria-label", "Salin kode");
					button.title = "Salin kode";
					button.innerHTML = clipboardIcon;
					resetTimers.delete(timer);
				}, 1800);
				resetTimers.add(timer);
			} catch {
				button.title = "Gagal menyalin";
			}
		};

		root.addEventListener("click", handleClick);
		return () => {
			root.removeEventListener("click", handleClick);
			resetTimers.forEach(clearTimeout);
		};
	}, [html]);

	return (
		<div
			ref={contentRef}
			className={className}
			style={style}
			dangerouslySetInnerHTML={{ __html: html }}
		/>
	);
}
